// src/components/kit/microsite/MicrositeBuilder.tsx
//
// Builder/editor for microsite pages. Split-pane on desktop, tabbed on mobile.
// Ported from the microsite mockup application's configurator.js.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  useMicrositePage,
  useCreateMicrositePage,
  useUpdateMicrositePage,
  useGetMicrositeUploadUrl,
} from '@/hooks/useMicrosite';
import type { MicrositeFormState, MicrositeLink, MicrositeEvent, LinkCategory } from '@/types/microsite';
import {
  LAYOUT_PRESETS,
  COLOR_PALETTES,
  FONT_THEMES,
  AVAILABLE_FONTS,
  LINK_CATEGORIES,
  BUTTON_STYLES,
  PROFILE_DISPLAY_MODES,
  createDefaultMicrositeFormState,
} from './themes';
import type { PaletteId, FontThemeId } from '@/types/microsite';
import MicrositeRenderer from './MicrositeRenderer';
import Spinner from '@/components/common/Spinner';

function generateId(): string {
  return `id_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export default function MicrositeBuilder() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const [previewDevice, setPreviewDevice] = useState<'mobile' | 'desktop'>('mobile');

  const { data: pageData, isLoading } = useMicrositePage();
  const createMutation = useCreateMicrositePage();
  const updateMutation = useUpdateMicrositePage();
  const uploadMutation = useGetMicrositeUploadUrl();

  const isExisting = !!pageData?.page;

  const [form, setForm] = useState<MicrositeFormState>(createDefaultMicrositeFormState());
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize form from existing page data
  useEffect(() => {
    if (pageData?.page) {
      const p = pageData.page;
      setForm({
        slug: p.slug,
        artist_name: p.artist_name,
        show_hero_artist_name: p.show_hero_artist_name !== false,
        tagline: p.tagline,
        bio: p.bio,
        layout: p.layout,
        palette_id: p.palette_id,
        colors: { ...p.colors },
        font_theme_id: p.font_theme_id,
        fonts: { ...p.fonts },
        base_font_size: p.base_font_size,
        border_radius: p.border_radius,
        button_style: p.button_style,
        hero_image_key: null,
        hero_image_url: p.hero_image_url,
        profile_image_key: null,
        profile_image_url: p.profile_image_url,
        featured_image_key: null,
        featured_image_url: p.featured_image_url,
        profile_display_mode: p.profile_display_mode,
        featured_title: p.featured_title,
        featured_subtitle: p.featured_subtitle,
        featured_url: p.featured_url || '',
        links: p.links,
        show_events: p.show_events,
        events: p.events,
        email_capture: { ...p.email_capture },
        published: p.published,
      });
      setHasUnsavedChanges(false);
    }
  }, [pageData]);

  // Warn before closing with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) e.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const updateField = useCallback(<K extends keyof MicrositeFormState>(key: K, value: MicrositeFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  }, []);

  // Image upload helper
  const handleImageUpload = async (file: File, keyField: keyof MicrositeFormState, urlField: keyof MicrositeFormState) => {
    try {
      const result = await uploadMutation.mutateAsync({ filename: file.name, contentType: file.type });
      await fetch(result.upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      updateField(keyField as any, result.object_key);
      updateField(urlField as any, result.public_url);
    } catch (err) {
      console.error('Image upload failed:', err);
    }
  };

  // Image clear helper
  const handleImageClear = (keyField: keyof MicrositeFormState, urlField: keyof MicrositeFormState) => {
    updateField(keyField as any, null);
    updateField(urlField as any, null);
  };

  // Link image upload helper
  const handleLinkImageUpload = async (file: File, linkIndex: number) => {
    try {
      const result = await uploadMutation.mutateAsync({ filename: file.name, contentType: file.type });
      await fetch(result.upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      updateLink(linkIndex, { image_url: result.public_url });
    } catch (err) {
      console.error('Link image upload failed:', err);
    }
  };

  // Link helpers
  const addLink = () => {
    if (form.links.length >= 10) return;
    const newLink: MicrositeLink = {
      link_id: generateId(), label: 'New Link', url: 'https://', category: 'other', image_url: null, position: form.links.length,
    };
    updateField('links', [...form.links, newLink]);
  };

  const updateLink = (index: number, updates: Partial<MicrositeLink>) => {
    const newLinks = [...form.links];
    newLinks[index] = { ...newLinks[index], ...updates };
    updateField('links', newLinks);
  };

  const removeLink = (index: number) => {
    updateField('links', form.links.filter((_, i) => i !== index).map((l, i) => ({ ...l, position: i })));
  };

  const moveLink = (index: number, direction: 'up' | 'down') => {
    const newLinks = [...form.links];
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= newLinks.length) return;
    [newLinks[index], newLinks[target]] = [newLinks[target], newLinks[index]];
    updateField('links', newLinks.map((l, i) => ({ ...l, position: i })));
  };

  // Event helpers
  const addEvent = () => {
    if (form.events.length >= 10) return;
    const newEvent: MicrositeEvent = {
      event_id: generateId(), date: new Date().toISOString().split('T')[0], venue: '', city: '', ticket_url: '',
    };
    updateField('events', [...form.events, newEvent]);
  };

  const updateEvent = (index: number, updates: Partial<MicrositeEvent>) => {
    const newEvents = [...form.events];
    newEvents[index] = { ...newEvents[index], ...updates };
    updateField('events', newEvents);
  };

  const removeEvent = (index: number) => {
    updateField('events', form.events.filter((_, i) => i !== index));
  };

  // Navigate with guard
  const navigateWithGuard = (path: string) => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) return;
    }
    navigate(path);
  };

  // Save / Publish
  const handleSave = async (publish: boolean) => {
    setSaveStatus('saving');
    setSaveError(null);
    try {
      const payload = { ...form, published: publish };
      if (isExisting) {
        await updateMutation.mutateAsync(payload);
      } else {
        await createMutation.mutateAsync(payload);
      }
      setSaveStatus('saved');
      setHasUnsavedChanges(false);
      setTimeout(() => setSaveStatus('idle'), 2000);
      if (!isExisting) navigate('/app/kit/microsite');
    } catch (err: any) {
      setSaveStatus('error');
      if (err?.code === 'SLUG_TAKEN') {
        setSaveError('This URL slug is already in use. Please choose a different one.');
      } else {
        setSaveError(err?.message || 'Something went wrong. Please try again.');
      }
    }
  };

  if (isLoading) {
    return <div className="flex h-full items-center justify-center"><Spinner /></div>;
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // ---- Form Content ----
  const formContent = (
    <div className="space-y-6 p-6">
      {/* Back button */}
      <button onClick={() => navigateWithGuard('/app/kit/microsite')} className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700">
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Back
      </button>

      <h2 className="text-lg font-bold text-neutral-800">{isExisting ? 'Edit Microsite' : 'Create Microsite'}</h2>

      {/* Profile Section */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Profile</h3>
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">URL Slug</label>
          <div className="flex items-center gap-1">
            <span className="flex-shrink-0 text-xs text-neutral-500">site.bandjerry.com/</span>
            <input type="text" value={form.slug} onChange={(e) => { updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); if (saveError) setSaveError(null); }} placeholder="your-name" className="min-w-0 flex-1 rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none" />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">Artist Name</label>
          <input type="text" value={form.artist_name} onChange={(e) => updateField('artist_name', e.target.value)} placeholder="Your artist name" maxLength={100} className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none" />
          <label className="mt-2 flex items-center gap-3">
            <input type="checkbox" checked={form.show_hero_artist_name} onChange={(e) => updateField('show_hero_artist_name', e.target.checked)} className="h-4 w-4 rounded border-neutral-400 accent-primary-600" />
            <span className="text-xs text-neutral-600">Show artist name in hero banner</span>
          </label>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">Tagline</label>
          <input type="text" value={form.tagline} onChange={(e) => updateField('tagline', e.target.value)} placeholder="Short bio or description" maxLength={200} className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">Bio</label>
          <textarea value={form.bio} onChange={(e) => updateField('bio', e.target.value)} placeholder="Tell your story..." maxLength={2000} rows={4} className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none" />
        </div>
      </section>

      {/* Layout */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Layout</h3>
        <div className="space-y-2">
          {Object.values(LAYOUT_PRESETS).map((preset) => (
            <button key={preset.id} onClick={() => updateField('layout', preset.id)} className={`block w-full rounded-lg border p-3 text-left transition-colors ${form.layout === preset.id ? 'border-primary-600 bg-primary-100/20' : 'border-neutral-300 hover:border-neutral-400'}`}>
              <p className="text-sm font-medium text-neutral-700">{preset.name}</p>
              <p className="text-xs text-neutral-500">{preset.description}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Color Palette */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Color Palette</h3>
        <div className="grid grid-cols-2 gap-2">
          {(Object.keys(COLOR_PALETTES) as Exclude<PaletteId, 'custom'>[]).map((key) => {
            const pal = COLOR_PALETTES[key];
            return (
              <button key={key} onClick={() => { updateField('palette_id', key); updateField('colors', { ...pal.colors }); }} className={`flex flex-col items-center rounded-lg border p-3 transition-colors ${form.palette_id === key ? 'border-primary-600 bg-primary-100/20' : 'border-neutral-300 hover:border-neutral-400'}`}>
                <div className="mb-1 flex gap-1">
                  {[pal.colors.background, pal.colors.surface, pal.colors.accent, pal.colors.primary, pal.colors.text].map((c, i) => (
                    <span key={i} className="h-5 w-5 rounded border border-neutral-400" style={{ background: c }} />
                  ))}
                </div>
                <span className="text-xs font-medium text-neutral-700">{pal.name}</span>
              </button>
            );
          })}
        </div>
        <div className="space-y-2 pt-2">
          <p className="text-xs font-semibold uppercase text-neutral-500">Custom Colors</p>
          {(['background', 'surface', 'primary', 'accent', 'text', 'textMuted', 'buttonBg', 'buttonText'] as const).map((key) => (
            <div key={key} className="flex items-center justify-between">
              <label className="text-xs text-neutral-600">{key}</label>
              <input type="color" value={form.colors[key]} onChange={(e) => { updateField('colors', { ...form.colors, [key]: e.target.value }); updateField('palette_id', 'custom'); }} className="h-6 w-8 cursor-pointer rounded border border-neutral-300" />
            </div>
          ))}
        </div>
      </section>

      {/* Fonts */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Fonts</h3>
        <div className="space-y-2">
          {(Object.keys(FONT_THEMES) as Exclude<FontThemeId, 'custom'>[]).map((key) => {
            const theme = FONT_THEMES[key];
            return (
              <button key={key} onClick={() => { updateField('font_theme_id', key); updateField('fonts', { headingFont: theme.headingFont, bodyFont: theme.bodyFont }); }} className={`block w-full rounded-lg border p-3 text-left transition-colors ${form.font_theme_id === key ? 'border-primary-600 bg-primary-100/20' : 'border-neutral-300 hover:border-neutral-400'}`}>
                <p className="text-sm font-medium text-neutral-700">{theme.name}</p>
                <p className="text-xs text-neutral-500">{theme.headingFont} / {theme.bodyFont}</p>
              </button>
            );
          })}
        </div>
        <div className="space-y-2 pt-2">
          <p className="text-xs font-semibold uppercase text-neutral-500">Custom Fonts</p>
          {(['headingFont', 'bodyFont'] as const).map((key) => (
            <div key={key}>
              <label className="mb-1 block text-xs text-neutral-600">{key === 'headingFont' ? 'Heading Font' : 'Body Font'}</label>
              <select value={form.fonts[key]} onChange={(e) => { updateField('fonts', { ...form.fonts, [key]: e.target.value }); updateField('font_theme_id', 'custom'); }} className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 focus:border-primary-600 focus:outline-none">
                {AVAILABLE_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          ))}
        </div>
      </section>

      {/* Style Tweaks */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Style Tweaks</h3>
        <div>
          <div className="flex justify-between text-xs text-neutral-600"><span>Base Font Size</span><span className="font-semibold text-primary-600">{form.base_font_size}px</span></div>
          <input type="range" min={12} max={22} step={1} value={form.base_font_size} onChange={(e) => updateField('base_font_size', Number(e.target.value))} className="w-full accent-primary-600" />
        </div>
        <div>
          <div className="flex justify-between text-xs text-neutral-600"><span>Border Radius</span><span className="font-semibold text-primary-600">{form.border_radius}px</span></div>
          <input type="range" min={0} max={28} step={1} value={form.border_radius} onChange={(e) => updateField('border_radius', Number(e.target.value))} className="w-full accent-primary-600" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-neutral-600">Button Style</label>
          <select value={form.button_style} onChange={(e) => updateField('button_style', e.target.value as any)} className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 focus:border-primary-600 focus:outline-none">
            {BUTTON_STYLES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>
      </section>

      {/* Featured */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Featured</h3>
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">Featured Title</label>
          <input type="text" value={form.featured_title} onChange={(e) => updateField('featured_title', e.target.value)} placeholder="New Album — Title" maxLength={200} className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">Featured Subtitle</label>
          <input type="text" value={form.featured_subtitle} onChange={(e) => updateField('featured_subtitle', e.target.value)} placeholder="Out now on all platforms" maxLength={200} className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">Featured Link URL <span className="font-normal text-neutral-500">(optional)</span></label>
          <input type="url" value={form.featured_url} onChange={(e) => updateField('featured_url', e.target.value)} placeholder="https://..." className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none" />
          <p className="mt-1 text-xs text-neutral-500">If provided, clicking the featured section will open this link.</p>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">Featured Image</label>
          <div className="flex items-center gap-2">
            <label className="cursor-pointer rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-300">
              {form.featured_image_url ? 'Change' : 'Upload'}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageUpload(f, 'featured_image_key', 'featured_image_url'); }} />
            </label>
            {form.featured_image_url && (
              <button onClick={() => handleImageClear('featured_image_key', 'featured_image_url')} className="text-xs text-red-400 hover:text-red-300">Remove</button>
            )}
          </div>
          {form.featured_image_url && <img src={form.featured_image_url} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
        </div>
      </section>

      {/* Links */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Links</h3>
          <button onClick={addLink} disabled={form.links.length >= 10} className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-black transition-colors hover:bg-primary-700 disabled:opacity-50">+ Add Link</button>
        </div>
        {form.links.map((link, index) => (
          <div key={link.link_id} className="space-y-2 rounded-lg border border-neutral-300 bg-neutral-100 p-3">
            <div className="flex items-center justify-between">
              <select value={link.category} onChange={(e) => { const cat = LINK_CATEGORIES.find((c) => c.id === e.target.value); updateLink(index, { category: e.target.value as LinkCategory, label: cat?.label || link.label }); }} className="w-28 rounded border border-neutral-300 bg-neutral-200 px-2 py-1 text-xs text-neutral-700">
                {LINK_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <div className="flex gap-1">
                <button onClick={() => moveLink(index, 'up')} disabled={index === 0} className="rounded p-1 text-neutral-500 hover:bg-neutral-300 disabled:opacity-30"><svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg></button>
                <button onClick={() => moveLink(index, 'down')} disabled={index === form.links.length - 1} className="rounded p-1 text-neutral-500 hover:bg-neutral-300 disabled:opacity-30"><svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg></button>
                <button onClick={() => removeLink(index)} className="rounded p-1 text-red-400 hover:bg-red-900/20"><svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
              </div>
            </div>
            <input type="text" value={link.label} onChange={(e) => updateLink(index, { label: e.target.value })} placeholder="Label" maxLength={100} className="w-full rounded-lg border border-neutral-300 bg-neutral-200 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none" />
            <input type="url" value={link.url} onChange={(e) => updateLink(index, { url: e.target.value })} placeholder="https://..." className="w-full rounded-lg border border-neutral-300 bg-neutral-200 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none" />
            {/* Link image upload */}
            <div className="flex items-center gap-2">
              <label className="cursor-pointer rounded border border-neutral-300 px-2 py-1 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-300">
                {link.image_url ? 'Change Image' : 'Add Image'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLinkImageUpload(f, index); }} />
              </label>
              {link.image_url && (
                <>
                  <img src={link.image_url} alt="" className="h-8 w-8 rounded object-cover" />
                  <button onClick={() => updateLink(index, { image_url: null })} className="text-xs text-red-400 hover:text-red-300">Remove</button>
                </>
              )}
            </div>
          </div>
        ))}
        {form.links.length === 0 && <p className="text-center text-sm text-neutral-500">No links yet. Add your first link above.</p>}
      </section>

      {/* Events */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Events</h3>
          <button onClick={addEvent} disabled={form.events.length >= 10} className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-black transition-colors hover:bg-primary-700 disabled:opacity-50">+ Add Event</button>
        </div>
        <label className="flex items-center gap-3">
          <input type="checkbox" checked={form.show_events} onChange={(e) => updateField('show_events', e.target.checked)} className="h-4 w-4 rounded border-neutral-400 accent-primary-600" />
          <span className="text-sm text-neutral-700">Show events section</span>
        </label>
        {form.events.map((evt, index) => (
          <div key={evt.event_id} className="space-y-2 rounded-lg border border-neutral-300 bg-neutral-100 p-3">
            <div className="flex items-center gap-2">
              <input type="date" value={evt.date} onChange={(e) => updateEvent(index, { date: e.target.value })} className="flex-1 rounded border border-neutral-300 bg-neutral-200 px-2 py-1 text-xs text-neutral-700" />
              <button onClick={() => removeEvent(index)} className="rounded p-1 text-red-400 hover:bg-red-900/20"><svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
            </div>
            <input type="text" value={evt.venue} onChange={(e) => updateEvent(index, { venue: e.target.value })} placeholder="Venue" className="w-full rounded-lg border border-neutral-300 bg-neutral-200 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none" />
            <input type="text" value={evt.city} onChange={(e) => updateEvent(index, { city: e.target.value })} placeholder="City, ST" className="w-full rounded-lg border border-neutral-300 bg-neutral-200 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none" />
            <input type="text" value={evt.ticket_url} onChange={(e) => updateEvent(index, { ticket_url: e.target.value })} placeholder="Ticket URL" className="w-full rounded-lg border border-neutral-300 bg-neutral-200 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none" />
          </div>
        ))}
      </section>

      {/* Email Capture */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Email Capture</h3>
        <label className="flex items-center gap-3">
          <input type="checkbox" checked={form.email_capture.enabled} onChange={(e) => updateField('email_capture', { ...form.email_capture, enabled: e.target.checked })} className="h-4 w-4 rounded border-neutral-400 accent-primary-600" />
          <span className="text-sm text-neutral-700">Enable email signup form</span>
        </label>
        {form.email_capture.enabled && (
          <>
            <input type="text" value={form.email_capture.prompt_text} onChange={(e) => updateField('email_capture', { ...form.email_capture, prompt_text: e.target.value })} placeholder="Join my mailing list" maxLength={100} className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none" />
            <input type="text" value={form.email_capture.button_label} onChange={(e) => updateField('email_capture', { ...form.email_capture, button_label: e.target.value })} placeholder="Subscribe" maxLength={30} className="w-full rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-500 focus:border-primary-600 focus:outline-none" />
          </>
        )}
      </section>

      {/* Images */}
      <section className="space-y-4 rounded-xl border border-neutral-300 bg-neutral-200 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Images</h3>
        {/* Hero Image */}
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">Hero Image</label>
          <div className="flex items-center gap-2">
            <label className="cursor-pointer rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-300">
              {form.hero_image_url ? 'Change' : 'Upload'}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageUpload(f, 'hero_image_key', 'hero_image_url'); }} />
            </label>
            {form.hero_image_url && (
              <button onClick={() => handleImageClear('hero_image_key', 'hero_image_url')} className="text-xs text-red-400 hover:text-red-300">Remove</button>
            )}
          </div>
          {form.hero_image_url && <img src={form.hero_image_url} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
        </div>
        {/* Profile Display Mode */}
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">Profile Display</label>
          <div className="grid grid-cols-2 gap-2">
            {PROFILE_DISPLAY_MODES.map((mode) => (
              <button key={mode.id} onClick={() => updateField('profile_display_mode', mode.id)} className={`rounded-lg border p-2 text-left transition-colors ${form.profile_display_mode === mode.id ? 'border-primary-600 bg-primary-100/20' : 'border-neutral-300 hover:border-neutral-400'}`}>
                <p className="text-xs font-medium text-neutral-700">{mode.name}</p>
                <p className="text-xs text-neutral-500">{mode.description}</p>
              </button>
            ))}
          </div>
        </div>
        {/* Profile Image */}
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">Profile Image</label>
          <div className="flex items-center gap-2">
            <label className="cursor-pointer rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-300">
              {form.profile_image_url ? 'Change' : 'Upload'}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageUpload(f, 'profile_image_key', 'profile_image_url'); }} />
            </label>
            {form.profile_image_url && (
              <button onClick={() => handleImageClear('profile_image_key', 'profile_image_url')} className="text-xs text-red-400 hover:text-red-300">Remove</button>
            )}
          </div>
          {form.profile_image_url && <img src={form.profile_image_url} alt="" className="mt-2 h-16 w-16 rounded-full object-cover" />}
        </div>
      </section>

      {/* Save error */}
      {saveError && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3">
          <p className="text-sm font-medium text-red-700">{saveError}</p>
        </div>
      )}

      {/* Action button */}
      <div className="pb-6">
        <button onClick={() => handleSave(true)} disabled={isSaving} className="w-full rounded-lg bg-primary-600 py-3 text-sm font-semibold text-black transition-colors hover:bg-primary-700 disabled:opacity-50">
          {saveStatus === 'saving' ? 'Publishing...' : saveStatus === 'saved' ? '✓ Published!' : 'Publish'}
        </button>
      </div>
    </div>
  );

  // ---- Preview Content ----
  const previewContent = (
    <div className="flex h-full flex-col">
      {!isMobile && (
        <div className="flex items-center justify-center gap-2 border-b border-neutral-300 bg-neutral-200 p-2">
          <button onClick={() => setPreviewDevice('mobile')} className={`rounded-md px-3 py-1.5 text-xs font-medium ${previewDevice === 'mobile' ? 'bg-neutral-300 text-neutral-800' : 'text-neutral-500'}`}>📱 Mobile</button>
          <button onClick={() => setPreviewDevice('desktop')} className={`rounded-md px-3 py-1.5 text-xs font-medium ${previewDevice === 'desktop' ? 'bg-neutral-300 text-neutral-800' : 'text-neutral-500'}`}>🖥️ Desktop</button>
        </div>
      )}
      <div className="flex flex-1 items-start justify-center overflow-y-auto bg-neutral-100 p-4">
        <div className={`overflow-hidden rounded-2xl border-4 border-neutral-400 shadow-xl ${previewDevice === 'mobile' ? 'w-[390px]' : 'w-full max-w-2xl'}`} style={previewDevice === 'mobile' ? { height: '700px' } : { minHeight: '600px' }}>
          <div className="h-full overflow-y-auto">
            <MicrositeRenderer data={form} isPreview />
          </div>
        </div>
      </div>
    </div>
  );

  // Mobile: tabbed layout
  if (isMobile) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex border-b border-neutral-300 bg-neutral-200">
          <button onClick={() => setActiveTab('edit')} className={`flex-1 py-3 text-center text-sm font-medium ${activeTab === 'edit' ? 'border-b-2 border-primary-600 text-primary-600' : 'text-neutral-500'}`}>Edit</button>
          <button onClick={() => setActiveTab('preview')} className={`flex-1 py-3 text-center text-sm font-medium ${activeTab === 'preview' ? 'border-b-2 border-primary-600 text-primary-600' : 'text-neutral-500'}`}>Preview</button>
        </div>
        <div className="flex-1 overflow-y-auto">{activeTab === 'edit' ? formContent : previewContent}</div>
      </div>
    );
  }

  // Desktop: split pane
  return (
    <div className="flex h-full">
      <div className="w-1/2 overflow-y-auto border-r border-neutral-300">{formContent}</div>
      <div className="w-1/2">{previewContent}</div>
    </div>
  );
}
